(() => {
  if (customElements.get('senza-unique-sets')) return;
  class SenzaUniqueSets extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialogs = [];
      this.activeDialog = null;
      this.addEventListener('click', event => this.handleClick(event), options);
      this.setupScrollFill(options);
      const supportsDialog = typeof HTMLDialogElement !== 'undefined' && typeof HTMLDialogElement.prototype.showModal === 'function';
      if (!supportsDialog) return;
      this.querySelectorAll('[data-detail]').forEach(panel => {
        const dialog = document.createElement('dialog');
        for (const attribute of panel.attributes) dialog.setAttribute(attribute.name, attribute.value);
        dialog.append(...panel.childNodes);
        panel.replaceWith(dialog);
        dialog.querySelector('[data-close]').hidden = false;
        const thumbs = dialog.querySelector('[data-thumbs]');
        thumbs.hidden = thumbs.children.length < 2;
        dialog.addEventListener('close', () => this.afterClose(dialog), options);
        this.dialogs.push(dialog);
      });
      this.classList.add('is-enhanced');
      const filters = this.querySelector('[data-filters]');
      if (filters) filters.hidden = false;
      window.addEventListener('hashchange', () => this.openFromHash(), options);
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const card = event.target.closest('[data-set]');
        if (!card) return;
        this.setFilter('all');
        this.open(card.querySelector('dialog'));
      }, options);
      document.addEventListener('shopify:section:unload', event => {
        if (event.detail.sectionId === this.dataset.sectionId) this.disconnectedCallback();
      }, options);
      this.openFromHash();
    }
    disconnectedCallback() {
      if (this.activeDialog) {
        const previous = this.activeDialog;
        previous.close();
        this.afterClose(previous);
      }
      this.restoreScroll();
      if (this.fillFrame) cancelAnimationFrame(this.fillFrame);
      this.fillFrame = null;
      this.controller?.abort();
      this.controller = null;
    }
    setupScrollFill(options) {
      const lead = this.querySelector('[data-scroll-fill]');
      if (!lead || typeof window.matchMedia !== 'function' || typeof window.requestAnimationFrame !== 'function') return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const text = lead.textContent.trim();
      lead.replaceChildren();
      const words = text.split(/\s+/).map((word, index) => {
        if (index) lead.append(document.createTextNode(' '));
        const span = document.createElement('span');
        span.className = 'sz-sets__fill-word';
        span.textContent = word;
        lead.append(span);
        return span;
      });
      const update = () => {
        this.fillFrame = null;
        const rect = lead.getBoundingClientRect();
        const viewport = window.innerHeight;
        const scroll = window.scrollY;
        const start = Math.max(0, rect.top + scroll - viewport * 0.72);
        const distance = Math.max(180, rect.height + viewport * 0.18);
        const progress = reducedMotion.matches ? 1 : Math.min(1, Math.max(0, (scroll - start) / distance));
        words.forEach((word, index) => {
          const fill = Math.min(1, Math.max(0, progress * words.length - index));
          word.style.setProperty('--word-fill', `${(fill * 100).toFixed(2)}%`);
        });
      };
      const schedule = () => {
        if (!this.fillFrame) this.fillFrame = window.requestAnimationFrame(update);
      };
      window.addEventListener('scroll', schedule, { ...options, passive: true });
      window.addEventListener('resize', schedule, options);
      reducedMotion.addEventListener('change', schedule, options);
      document.fonts?.ready.then(() => { if (!options.signal.aborted) schedule(); });
      update();
    }
    handleClick(event) {
      const open = event.target.closest('[data-open]');
      if (open && this.classList.contains('is-enhanced')) {
        event.preventDefault();
        const dialog = this.dialogs.find(item => item.id === open.dataset.open);
        this.open(dialog, open);
        return;
      }
      const close = event.target.closest('[data-close]');
      if (close) close.closest('dialog')?.close();
      const filter = event.target.closest('[data-filter]');
      if (filter) this.setFilter(filter.dataset.filter);
      const photo = event.target.closest('[data-photo]');
      if (photo) {
        const gallery = photo.closest('.sz-sets__gallery');
        const main = gallery.querySelector('.sz-sets__selected-photo');
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
        main.src = photo.dataset.photo;
        main.alt = photo.dataset.alt;
        gallery.querySelectorAll('[data-photo]').forEach(button => button.setAttribute('aria-pressed', String(button === photo)));
      }
      if (event.target.tagName === 'DIALOG') {
        const rect = event.target.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
      }
    }
    setFilter(value) {
      let count = 0;
      this.querySelectorAll('[data-set]').forEach(card => {
        card.hidden = value !== 'all' && card.dataset.state !== value;
        if (!card.hidden) count++;
      });
      this.querySelectorAll('[data-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.filter === value)));
      this.querySelector('[data-filter-empty]').hidden = count > 0;
    }
    open(dialog, trigger) {
      if (!dialog || dialog.open) return;
      if (this.activeDialog) {
        const previous = this.activeDialog;
        previous.close();
        this.afterClose(previous);
      }
      this.trigger = trigger || dialog.closest('[data-set]')?.querySelector('[data-open]');
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.activeDialog = dialog;
      dialog.showModal();
      dialog.querySelector('[data-close]').focus({ preventScroll: true });
    }
    afterClose(dialog) {
      if (this.activeDialog !== dialog) return;
      this.activeDialog = null;
      this.restoreScroll();
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      this.trigger = null;
    }
    restoreScroll() {
      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
    }
    openFromHash() {
      let id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      if (!id) return;
      const card = [...this.querySelectorAll('[data-set]')].find(item => item.id === id || item.querySelector('dialog')?.id === id);
      if (!card) return;
      this.setFilter('all');
      this.open(card.querySelector('dialog'));
    }
  }
  customElements.define('senza-unique-sets', SenzaUniqueSets);
})();
